import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Input, Select, Badge } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const {
  FiMail,
  FiPlus,
  FiEdit2,
  FiCopy,
  FiTrash2,
  FiArrowLeft,
  FiMenu,
} = FiIcons;

const TRIGGER_STATUS_OPTIONS = [
  { value: '', label: 'Manual only' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'dnc', label: 'DNC' },
];

const TRIGGER_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  follow_up: 'Follow Up',
  appointment_set: 'Appointment Set',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  dnc: 'DNC',
};

const MERGE_FIELDS = [
  '{{first_name}}',
  '{{last_name}}',
  '{{agent_name}}',
  '{{agency_name}}',
  '{{phone}}',
];

const emptySequenceForm = () => ({
  name: '',
  description: '',
  trigger_status: '',
  is_active: true,
});

const emptyStepForm = () => ({
  delay_days: 0,
  subject: '',
  body_html: '',
});

function ToggleSwitch({ checked, onChange, disabled, 'aria-labelledby': ariaLabelledby }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={ariaLabelledby}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function MergeFieldChips({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {MERGE_FIELDS.map((token) => (
        <button
          key={token}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsert(token)}
          className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
        >
          {token}
        </button>
      ))}
    </div>
  );
}

function triggerLabel(triggerStatus) {
  if (!triggerStatus) return 'Manual only';
  return TRIGGER_LABELS[triggerStatus] || triggerStatus;
}

function isLocalStepId(id) {
  return typeof id === 'string' && id.startsWith('local-');
}

function StepForm({
  initial,
  onSave,
  onCancel,
  saving,
}) {
  const [delayDays, setDelayDays] = useState(initial.delay_days ?? 0);
  const [subject, setSubject] = useState(initial.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(initial.body_html ?? '');
  const bodyRef = useRef(null);

  const insertMergeField = (token) => {
    const el = bodyRef.current;
    setBodyHtml((prev) => {
      const start = el?.selectionStart ?? prev.length;
      const end = el?.selectionEnd ?? start;
      const next = prev.slice(0, start) + token + prev.slice(end);
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim()) return;
    onSave({
      delay_days: Number(delayDays) || 0,
      subject: subject.trim(),
      body_html: bodyHtml,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-primary/30 bg-blue-50/40 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input
          label="Delay (days after previous)"
          type="number"
          min={0}
          value={delayDays}
          onChange={(e) => setDelayDays(e.target.value)}
        />
        <div className="sm:col-span-2">
          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email body (HTML)</label>
        <textarea
          ref={bodyRef}
          rows={5}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <MergeFieldChips onInsert={insertMergeField} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" isLoading={saving} disabled={!subject.trim()}>
          Save Step
        </Button>
      </div>
    </form>
  );
}

function StepRow({
  step,
  index,
  onEdit,
  onDelete,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
}) {
  const delayLabel =
    index === 0
      ? step.delay_days === 0
        ? 'Immediately'
        : `${step.delay_days} day${step.delay_days === 1 ? '' : 's'} after enroll`
      : `${step.delay_days} day${step.delay_days === 1 ? '' : 's'} after previous`;

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:border-gray-300 transition-colors"
    >
      <button
        type="button"
        className="mt-1 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
        aria-label="Drag to reorder"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SafeIcon icon={FiMenu} className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-secondary bg-gray-100 px-2 py-0.5 rounded">
            Step {step.step_number ?? index + 1}
          </span>
          <span className="text-xs text-gray-500">{delayLabel}</span>
        </div>
        <p className="text-sm font-medium text-gray-900 truncate">{step.subject || '(No subject)'}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(step)}>
          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(step)}>
          <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

export default function SequencesTab() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [view, setView] = useState('list');
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySequenceForm());
  const [steps, setSteps] = useState([]);
  const [savingSequence, setSavingSequence] = useState(false);

  const [stepFormMode, setStepFormMode] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [savingStep, setSavingStep] = useState(false);
  const [dragStepId, setDragStepId] = useState(null);

  const loadSequences = useCallback(async () => {
    if (!user?.agency_id) {
      setSequences([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.sequences.getAll(user.agency_id);
      setSequences(rows);
    } catch (err) {
      console.error(err);
      addToast({
        type: 'error',
        title: 'Could not load sequences',
        message: err?.message || 'Try again in a moment.',
      });
      setSequences([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, addToast]);

  useEffect(() => {
    if (view === 'list') void loadSequences();
  }, [view, loadSequences]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptySequenceForm());
    setSteps([]);
    setStepFormMode(null);
    setEditingStep(null);
    setView('edit');
  };

  const openEdit = async (seq) => {
    setEditingId(seq.id);
    setForm({
      name: seq.name || '',
      description: seq.description || '',
      trigger_status: seq.trigger_status || '',
      is_active: seq.is_active !== false,
    });
    setStepFormMode(null);
    setEditingStep(null);
    setView('edit');
    setSavingSequence(true);
    try {
      const full = await api.sequences.getById(seq.id);
      if (full) {
        setForm({
          name: full.name || '',
          description: full.description || '',
          trigger_status: full.trigger_status || '',
          is_active: full.is_active !== false,
        });
        setSteps(full.steps || []);
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load sequence',
        message: err?.message || 'Try again.',
      });
      setView('list');
    } finally {
      setSavingSequence(false);
    }
  };

  const handleToggleActive = async (seq) => {
    setTogglingId(seq.id);
    try {
      await api.sequences.toggleActive(seq.id, !seq.is_active);
      setSequences((prev) =>
        prev.map((s) => (s.id === seq.id ? { ...s, is_active: !seq.is_active } : s)),
      );
      addToast({ type: 'success', title: seq.is_active ? 'Sequence deactivated' : 'Sequence activated' });
    } catch {
      addToast({ type: 'error', title: 'Failed to update sequence' });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDuplicateSequence = async (seq) => {
    setDuplicatingId(seq.id);
    try {
      await api.sequences.duplicate(seq.id);
      await loadSequences();
      addToast({ type: 'success', title: 'Sequence duplicated successfully' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to duplicate sequence',
        message: err?.message || 'Try again.',
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDeleteSequence = async (seq) => {
    if (!window.confirm(`Delete "${seq.name}"? This cannot be undone.`)) return;
    setDeletingId(seq.id);
    try {
      await api.sequences.delete(seq.id);
      setSequences((prev) => prev.filter((s) => s.id !== seq.id));
      addToast({ type: 'success', title: 'Sequence deleted' });
    } catch {
      addToast({ type: 'error', title: 'Failed to delete sequence' });
    } finally {
      setDeletingId(null);
    }
  };

  const persistStepOrder = async (orderedSteps) => {
    if (!editingId) return;
    const ids = orderedSteps.map((s) => s.id).filter((id) => !isLocalStepId(id));
    if (ids.length !== orderedSteps.length) return;
    try {
      await api.sequences.reorderSteps(editingId, ids);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not reorder steps', message: err?.message });
      throw err;
    }
  };

  const handleStepDragStart = (stepId) => setDragStepId(stepId);

  const handleStepDragOver = (e) => {
    e.preventDefault();
  };

  const handleStepDrop = async (targetStepId) => {
    if (!dragStepId || dragStepId === targetStepId) return;
    const fromIdx = steps.findIndex((s) => s.id === dragStepId);
    const toIdx = steps.findIndex((s) => s.id === targetStepId);
    if (fromIdx < 0 || toIdx < 0) return;

    const reordered = [...steps];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withNumbers = reordered.map((s, i) => ({ ...s, step_number: i + 1 }));
    setSteps(withNumbers);
    setDragStepId(null);

    if (editingId) {
      try {
        await persistStepOrder(withNumbers);
      } catch {
        const full = await api.sequences.getById(editingId);
        setSteps(full?.steps || []);
      }
    }
  };

  const handleSaveStep = async (stepPayload) => {
    setSavingStep(true);
    try {
      if (stepFormMode === 'edit' && editingStep) {
        if (isLocalStepId(editingStep.id)) {
          setSteps((prev) =>
            prev.map((s) =>
              s.id === editingStep.id
                ? { ...s, ...stepPayload, step_number: s.step_number }
                : s,
            ),
          );
        } else {
          const updated = await api.sequences.updateStep(editingStep.id, stepPayload);
          setSteps((prev) => prev.map((s) => (s.id === editingStep.id ? { ...s, ...updated } : s)));
        }
        addToast({ type: 'success', title: 'Step updated' });
      } else {
        const nextNumber = steps.length + 1;
        if (editingId) {
          const created = await api.sequences.createStep({
            sequence_id: editingId,
            agency_id: user.agency_id,
            step_number: nextNumber,
            ...stepPayload,
          });
          setSteps((prev) => [...prev, created]);
        } else {
          const localId = `local-${crypto.randomUUID()}`;
          setSteps((prev) => [
            ...prev,
            {
              id: localId,
              step_number: nextNumber,
              ...stepPayload,
            },
          ]);
        }
        addToast({ type: 'success', title: 'Step added' });
      }
      setStepFormMode(null);
      setEditingStep(null);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to save step',
        message: err?.message || 'Try again.',
      });
    } finally {
      setSavingStep(false);
    }
  };

  const handleDeleteStep = async (step) => {
    if (!window.confirm('Delete this step?')) return;
    if (isLocalStepId(step.id)) {
      setSteps((prev) => {
        const filtered = prev.filter((s) => s.id !== step.id);
        return filtered.map((s, i) => ({ ...s, step_number: i + 1 }));
      });
      return;
    }
    try {
      await api.sequences.deleteStep(step.id);
      const remaining = steps.filter((s) => s.id !== step.id).map((s, i) => ({ ...s, step_number: i + 1 }));
      setSteps(remaining);
      if (editingId && remaining.length) {
        await persistStepOrder(remaining);
      }
      addToast({ type: 'success', title: 'Step deleted' });
    } catch {
      addToast({ type: 'error', title: 'Failed to delete step' });
    }
  };

  const handleSaveSequence = async () => {
    if (!form.name.trim()) {
      addToast({ type: 'warning', title: 'Name is required' });
      return;
    }
    setSavingSequence(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        trigger_status: form.trigger_status || null,
        is_active: form.is_active,
        agency_id: user.agency_id,
      };

      let sequenceId = editingId;
      if (editingId) {
        await api.sequences.update(editingId, payload);
        addToast({ type: 'success', title: 'Sequence saved' });
      } else {
        const created = await api.sequences.create(payload);
        sequenceId = created.id;
        setEditingId(created.id);
        for (let i = 0; i < steps.length; i++) {
          const step = steps[i];
          await api.sequences.createStep({
            sequence_id: sequenceId,
            agency_id: user.agency_id,
            step_number: i + 1,
            delay_days: step.delay_days ?? 0,
            subject: step.subject,
            body_html: step.body_html,
          });
        }
        addToast({ type: 'success', title: 'Sequence created' });
      }

      setView('list');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to save sequence',
        message: err?.message || 'Try again.',
      });
    } finally {
      setSavingSequence(false);
    }
  };

  if (view === 'edit') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => setView('list')}>
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? 'Edit Sequence' : 'New Sequence'}
          </h2>
        </div>

        <Card className="p-6 space-y-5">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <Select
            label="Trigger status"
            value={form.trigger_status}
            onChange={(e) => setForm((f) => ({ ...f, trigger_status: e.target.value }))}
            options={TRIGGER_STATUS_OPTIONS}
          />
          <div className="flex items-center justify-between gap-4">
            <span id="seq-active-label" className="text-sm font-medium text-gray-800">
              Active
            </span>
            <ToggleSwitch
              checked={form.is_active}
              onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              aria-labelledby="seq-active-label"
            />
          </div>

          <div className="border-t border-gray-100 pt-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Steps</h3>
              {!stepFormMode && (
                <Button type="button" size="sm" variant="outline" onClick={() => setStepFormMode('add')}>
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                  Add Step
                </Button>
              )}
            </div>

            {stepFormMode && (
              <StepForm
                initial={stepFormMode === 'edit' && editingStep ? editingStep : emptyStepForm()}
                onSave={handleSaveStep}
                onCancel={() => {
                  setStepFormMode(null);
                  setEditingStep(null);
                }}
                saving={savingStep}
              />
            )}

            {steps.length === 0 && !stepFormMode ? (
              <p className="text-sm text-gray-500 py-4 text-center">No steps yet. Add your first email step.</p>
            ) : (
              <div className="space-y-2">
                {steps.map((step, index) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    index={index}
                    draggable
                    onDragStart={() => handleStepDragStart(step.id)}
                    onDragOver={handleStepDragOver}
                    onDrop={() => void handleStepDrop(step.id)}
                    onEdit={(s) => {
                      setEditingStep(s);
                      setStepFormMode('edit');
                    }}
                    onDelete={(s) => void handleDeleteStep(s)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <Button type="button" onClick={() => void handleSaveSequence()} isLoading={savingSequence}>
              Save Sequence
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {sequences.length} sequence{sequences.length !== 1 ? 's' : ''}
        </p>
        <Button size="sm" onClick={openCreate}>
          <SafeIcon icon={FiPlus} className="mr-2 w-4 h-4" />
          New Sequence
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Spinner size="lg" className="text-primary" />
            <p className="text-sm text-gray-500">Loading sequences…</p>
          </div>
        ) : sequences.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon icon={FiMail} className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No email sequences yet</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              Create automated drip sequences or manual enroll-only campaigns for your leads.
            </p>
            <Button size="sm" className="mt-4" onClick={openCreate}>
              <SafeIcon icon={FiPlus} className="mr-2 w-4 h-4" />
              New Sequence
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Trigger</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Steps</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Active</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sequences.map((seq) => (
                <tr key={seq.id} className="bg-white hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{seq.name}</p>
                    {seq.description && (
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{seq.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={seq.trigger_status ? 'primary' : 'default'}>
                      {triggerLabel(seq.trigger_status)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{seq.step_count ?? 0}</td>
                  <td className="px-6 py-4">
                    {togglingId === seq.id ? (
                      <Spinner className="text-primary" />
                    ) : (
                      <ToggleSwitch
                        checked={seq.is_active !== false}
                        onChange={() => void handleToggleActive(seq)}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => void openEdit(seq)}>
                        <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleDuplicateSequence(seq)}
                        disabled={duplicatingId === seq.id || deletingId === seq.id}
                        isLoading={duplicatingId === seq.id}
                      >
                        <SafeIcon icon={FiCopy} className="w-4 h-4 mr-1" />
                        Duplicate
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleDeleteSequence(seq)}
                        disabled={deletingId === seq.id || duplicatingId === seq.id}
                        isLoading={deletingId === seq.id}
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-1 text-red-500" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
